import type {
  DerivedEdge,
  Face,
  HalfEdge,
  MeshData,
  MeshTopology,
  TransformData,
  Vec2,
  Vec3,
  Vertex,
} from "../types";
import {
  add3,
  average3,
  clamp,
  cross3,
  dot3,
  faceNormal,
  lerp3,
  length3,
  mul3,
  normalize3,
  rotate2,
  sub3,
  vecKey,
} from "./math";

export const KERNEL_VERSION = 1 as const;
const DEFAULT_MATERIAL = "material_default";

const identityTransform = (): TransformData => ({
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
  pivot: [0, 0, 0],
});

export const edgeKey = (a: number, b: number): string => a < b ? `${a}:${b}` : `${b}:${a}`;

export function cloneMesh(mesh: MeshData): MeshData {
  return structuredClone(mesh);
}

export function refreshEdgeIds(mesh: MeshData): MeshData {
  const result = cloneMesh(mesh);
  const keys = new Set<string>();
  for (const face of result.faces) {
    for (let i = 0; i < face.vertices.length; i += 1) {
      keys.add(edgeKey(face.vertices[i], face.vertices[(i + 1) % face.vertices.length]));
    }
  }
  const nextMap: Record<string, number> = {};
  for (const key of [...keys].sort()) {
    nextMap[key] = result.edgeIds[key] ?? result.nextEdgeId++;
  }
  result.edgeIds = nextMap;
  return result;
}

export function deriveTopology(mesh: MeshData): MeshTopology {
  const halfEdges: HalfEdge[] = [];
  const faceHalfEdges = new Map<number, HalfEdge[]>();
  const directed = new Map<string, HalfEdge>();
  const edgesByKey = new Map<string, DerivedEdge>();
  const vertexEdges = new Map<number, number[]>();
  const warnings: string[] = [];
  let nextHalfEdgeId = 1;

  for (const face of mesh.faces) {
    if (face.vertices.length < 3) {
      warnings.push(`Face ${face.id} has fewer than three vertices.`);
      continue;
    }
    const local: HalfEdge[] = [];
    for (let i = 0; i < face.vertices.length; i += 1) {
      const start = face.vertices[i];
      const end = face.vertices[(i + 1) % face.vertices.length];
      const key = edgeKey(start, end);
      const edgeId = mesh.edgeIds[key] ?? -1;
      const halfEdge: HalfEdge = {
        id: nextHalfEdgeId++,
        edgeId,
        faceId: face.id,
        start,
        end,
        next: 0,
        prev: 0,
      };
      local.push(halfEdge);
      halfEdges.push(halfEdge);
      directed.set(`${start}:${end}`, halfEdge);
      const existing = edgesByKey.get(key) ?? {
        id: edgeId,
        key,
        a: Math.min(start, end),
        b: Math.max(start, end),
        faceIds: [],
        halfEdgeIds: [],
      };
      existing.faceIds.push(face.id);
      existing.halfEdgeIds.push(halfEdge.id);
      edgesByKey.set(key, existing);
    }
    for (let i = 0; i < local.length; i += 1) {
      local[i].next = local[(i + 1) % local.length].id;
      local[i].prev = local[(i - 1 + local.length) % local.length].id;
    }
    faceHalfEdges.set(face.id, local);
  }

  for (const halfEdge of halfEdges) {
    const twin = directed.get(`${halfEdge.end}:${halfEdge.start}`);
    if (twin) halfEdge.twin = twin.id;
  }

  const edges = [...edgesByKey.values()];
  for (const edge of edges) {
    if (edge.faceIds.length > 2) warnings.push(`Edge ${edge.id} is non-manifold (${edge.faceIds.length} faces).`);
    for (const vertexId of [edge.a, edge.b]) {
      const current = vertexEdges.get(vertexId) ?? [];
      current.push(edge.id);
      vertexEdges.set(vertexId, current);
    }
  }
  return {
    halfEdges,
    edges,
    edgeById: new Map(edges.map((edge) => [edge.id, edge])),
    faceHalfEdges,
    vertexEdges,
    warnings,
  };
}

export function validateMesh(mesh: MeshData): string[] {
  const warnings = [...deriveTopology(mesh).warnings];
  const vertexIds = new Set(mesh.vertices.map((vertex) => vertex.id));
  const faceIds = new Set<number>();
  for (const face of mesh.faces) {
    if (faceIds.has(face.id)) warnings.push(`Duplicate face ID ${face.id}.`);
    faceIds.add(face.id);
    if (new Set(face.vertices).size !== face.vertices.length) warnings.push(`Face ${face.id} repeats a vertex.`);
    for (const vertexId of face.vertices) {
      if (!vertexIds.has(vertexId)) warnings.push(`Face ${face.id} references missing vertex ${vertexId}.`);
    }
    if (face.uvs.length !== face.vertices.length) warnings.push(`Face ${face.id} has incomplete UV coordinates.`);
  }
  return warnings;
}

function addGridFace(
  vertices: Vertex[],
  faces: Face[],
  vertexLookup: Map<string, number>,
  nextIds: { vertex: number; face: number },
  origin: Vec3,
  u: Vec3,
  v: Vec3,
  uSegments: number,
  vSegments: number,
  expectedNormal: Vec3,
): void {
  const ids: number[][] = [];
  for (let y = 0; y <= vSegments; y += 1) {
    const row: number[] = [];
    for (let x = 0; x <= uSegments; x += 1) {
      const point = add3(origin, add3(mul3(u, x / uSegments), mul3(v, y / vSegments)));
      const key = vecKey(point);
      let id = vertexLookup.get(key);
      if (id === undefined) {
        id = nextIds.vertex++;
        vertexLookup.set(key, id);
        vertices.push({ id, position: point });
      }
      row.push(id);
    }
    ids.push(row);
  }
  const positionById = new Map(vertices.map((vertex) => [vertex.id, vertex.position]));
  for (let y = 0; y < vSegments; y += 1) {
    for (let x = 0; x < uSegments; x += 1) {
      let polygon = [ids[y][x], ids[y][x + 1], ids[y + 1][x + 1], ids[y + 1][x]];
      const positions = polygon.map((id) => positionById.get(id)!);
      if (dot3(faceNormal(positions), expectedNormal) < 0) polygon = [...polygon].reverse();
      faces.push({
        id: nextIds.face++,
        vertices: polygon,
        uvs: [[0, 0], [1, 0], [1, 1], [0, 1]],
        materialId: DEFAULT_MATERIAL,
      });
    }
  }
}

export function createCubeMesh(
  name = "pCube1",
  size = 100,
  divisions: Vec3 = [1, 1, 1],
): MeshData {
  const [dx, dy, dz] = divisions.map((value) => Math.max(1, Math.round(value))) as Vec3;
  const h = size / 2;
  const vertices: Vertex[] = [];
  const faces: Face[] = [];
  const lookup = new Map<string, number>();
  const nextIds = { vertex: 1, face: 1 };

  addGridFace(vertices, faces, lookup, nextIds, [h, -h, -h], [0, 0, size], [0, size, 0], dz, dy, [1, 0, 0]);
  addGridFace(vertices, faces, lookup, nextIds, [-h, -h, h], [0, 0, -size], [0, size, 0], dz, dy, [-1, 0, 0]);
  addGridFace(vertices, faces, lookup, nextIds, [-h, h, -h], [size, 0, 0], [0, 0, size], dx, dz, [0, 1, 0]);
  addGridFace(vertices, faces, lookup, nextIds, [-h, -h, h], [size, 0, 0], [0, 0, -size], dx, dz, [0, -1, 0]);
  addGridFace(vertices, faces, lookup, nextIds, [-h, -h, h], [size, 0, 0], [0, size, 0], dx, dy, [0, 0, 1]);
  addGridFace(vertices, faces, lookup, nextIds, [h, -h, -h], [-size, 0, 0], [0, size, 0], dx, dy, [0, 0, -1]);

  return refreshEdgeIds({
    id: crypto.randomUUID(),
    name,
    vertices,
    faces,
    edgeIds: {},
    nextVertexId: nextIds.vertex,
    nextFaceId: nextIds.face,
    nextEdgeId: 1,
    transform: identityTransform(),
    visible: true,
  });
}

export function vertexMap(mesh: MeshData): Map<number, Vertex> {
  return new Map(mesh.vertices.map((vertex) => [vertex.id, vertex]));
}

export function getFaceNormal(mesh: MeshData, face: Face): Vec3 {
  const vertices = vertexMap(mesh);
  return faceNormal(face.vertices.map((id) => vertices.get(id)!.position));
}

export function meshBounds(mesh: MeshData): { min: Vec3; max: Vec3; center: Vec3; size: Vec3 } {
  if (!mesh.vertices.length) return { min: [0, 0, 0], max: [0, 0, 0], center: [0, 0, 0], size: [0, 0, 0] };
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const { position } of mesh.vertices) {
    for (let axis = 0; axis < 3; axis += 1) {
      min[axis] = Math.min(min[axis], position[axis]);
      max[axis] = Math.max(max[axis], position[axis]);
    }
  }
  const size = sub3(max, min);
  return { min, max, center: mul3(add3(min, max), 0.5), size };
}

function newVertex(mesh: MeshData, position: Vec3): number {
  const id = mesh.nextVertexId++;
  mesh.vertices.push({ id, position });
  return id;
}

function newFace(mesh: MeshData, vertices: number[], materialId: string, uvs?: Vec2[]): Face {
  return {
    id: mesh.nextFaceId++,
    vertices,
    materialId,
    uvs: uvs ?? vertices.map(() => [0, 0] as Vec2),
  };
}

export interface MeshOperationResult {
  mesh: MeshData;
  selectedIds: number[];
  lineage: Record<string, number[]>;
  warnings: string[];
}

export function extrudeFaces(
  source: MeshData,
  faceIds: number[],
  distance: number,
  divisions = 1,
  offset = 0,
  keepTogether = true,
): MeshOperationResult {
  const mesh = cloneMesh(source);
  const selected = new Set(faceIds);
  const selectedFaces = mesh.faces.filter((face) => selected.has(face.id));
  if (!selectedFaces.length) throw new Error("Select one or more faces before extruding.");
  const topology = deriveTopology(mesh);
  if (topology.warnings.some((warning) => warning.includes("non-manifold"))) {
    throw new Error("Extrude was stopped because the selected mesh contains non-manifold edges.");
  }
  divisions = Math.max(1, Math.round(divisions));
  const vertices = vertexMap(mesh);
  const selectedVertexIds = [...new Set(selectedFaces.flatMap((face) => face.vertices))];
  const normalsByVertex = new Map<number, Vec3[]>();
  for (const face of selectedFaces) {
    const normal = getFaceNormal(mesh, face);
    for (const id of face.vertices) {
      const list = normalsByVertex.get(id) ?? [];
      list.push(normal);
      normalsByVertex.set(id, list);
    }
  }
  const center = average3(selectedVertexIds.map((id) => vertices.get(id)!.position));
  const rings: Array<Map<number, number>> = [];
  for (let division = 1; division <= divisions; division += 1) {
    const t = division / divisions;
    const ring = new Map<number, number>();
    for (const id of selectedVertexIds) {
      const original = vertices.get(id)!.position;
      const normal = normalize3(average3(normalsByVertex.get(id) ?? [[0, 1, 0]]));
      const expanded = add3(original, mul3(normalize3(sub3(original, center)), offset * t));
      ring.set(id, newVertex(mesh, add3(expanded, mul3(normal, distance * t))));
    }
    rings.push(ring);
  }

  const topRing = rings[rings.length - 1];
  for (const face of selectedFaces) {
    face.vertices = face.vertices.map((id) => topRing.get(id)!);
  }

  const selectedTop = selectedFaces.map((face) => face.id);
  const boundaryHalfEdges = topology.halfEdges.filter((halfEdge) => {
    if (!selected.has(halfEdge.faceId)) return false;
    if (!halfEdge.twin) return true;
    const twin = topology.halfEdges.find((candidate) => candidate.id === halfEdge.twin);
    return !twin || !selected.has(twin.faceId);
  });

  for (const halfEdge of boundaryHalfEdges) {
    let previousA = halfEdge.start;
    let previousB = halfEdge.end;
    for (const ring of rings) {
      const nextA = ring.get(halfEdge.start)!;
      const nextB = ring.get(halfEdge.end)!;
      mesh.faces.push(newFace(mesh, [previousA, previousB, nextB, nextA], mesh.faces.find((face) => face.id === halfEdge.faceId)?.materialId ?? DEFAULT_MATERIAL));
      previousA = nextA;
      previousB = nextB;
    }
  }

  if (!keepTogether && selectedFaces.length > 1) {
    return {
      mesh: refreshEdgeIds(mesh),
      selectedIds: selectedTop,
      lineage: Object.fromEntries(faceIds.map((id) => [`face:${id}`, [id]])),
      warnings: ["Separate-face extrusion is represented as connected extrusion in this MVP build."],
    };
  }
  return {
    mesh: refreshEdgeIds(mesh),
    selectedIds: selectedTop,
    lineage: Object.fromEntries(faceIds.map((id) => [`face:${id}`, [id]])),
    warnings: [],
  };
}

export function extrudeBoundaryEdges(
  source: MeshData,
  edgeIds: number[],
  distance: number,
  divisions = 1,
): MeshOperationResult {
  const mesh = cloneMesh(source);
  const topology = deriveTopology(mesh);
  const selectedEdges = edgeIds.map((id) => topology.edgeById.get(id)).filter((edge): edge is DerivedEdge => Boolean(edge));
  if (!selectedEdges.length) throw new Error("Select one or more boundary edges before extruding.");
  if (selectedEdges.some((edge) => edge.faceIds.length !== 1)) throw new Error("Edge extrusion currently accepts boundary edges only to avoid creating non-manifold geometry.");
  divisions = Math.max(1, Math.round(divisions));
  const vertices = vertexMap(mesh);
  const vertexNormals = new Map<number, Vec3[]>();
  for (const edge of selectedEdges) {
    const face = mesh.faces.find((candidate) => candidate.id === edge.faceIds[0])!;
    const normal = getFaceNormal(mesh, face);
    for (const vertexId of [edge.a, edge.b]) {
      const list = vertexNormals.get(vertexId) ?? [];
      list.push(normal);
      vertexNormals.set(vertexId, list);
    }
  }
  const rings: Array<Map<number, number>> = [];
  for (let division = 1; division <= divisions; division += 1) {
    const t = division / divisions;
    const ring = new Map<number, number>();
    for (const [vertexId, normals] of vertexNormals) {
      ring.set(vertexId, newVertex(mesh, add3(vertices.get(vertexId)!.position, mul3(normalize3(average3(normals)), distance * t))));
    }
    rings.push(ring);
  }
  const createdFaces: number[] = [];
  for (const edge of selectedEdges) {
    let a = edge.a;
    let b = edge.b;
    for (const ring of rings) {
      const nextA = ring.get(edge.a)!;
      const nextB = ring.get(edge.b)!;
      const face = newFace(mesh, [a, b, nextB, nextA], DEFAULT_MATERIAL);
      mesh.faces.push(face);
      createdFaces.push(face.id);
      a = nextA;
      b = nextB;
    }
  }
  return { mesh: refreshEdgeIds(mesh), selectedIds: createdFaces, lineage: {}, warnings: [] };
}

function incidentFaceCornerPoint(
  mesh: MeshData,
  face: Face,
  vertexIndex: number,
  selectedEdgeKeys: Set<string>,
  width: number,
  positions: Map<number, Vertex>,
): Vec3 | null {
  const count = face.vertices.length;
  const id = face.vertices[vertexIndex];
  const previous = face.vertices[(vertexIndex - 1 + count) % count];
  const next = face.vertices[(vertexIndex + 1) % count];
  const incoming = selectedEdgeKeys.has(edgeKey(previous, id));
  const outgoing = selectedEdgeKeys.has(edgeKey(id, next));
  if (!incoming && !outgoing) return null;
  const point = positions.get(id)!.position;
  let result = point;
  if (incoming) result = add3(result, mul3(normalize3(sub3(positions.get(next)!.position, point)), width));
  if (outgoing) result = add3(result, mul3(normalize3(sub3(positions.get(previous)!.position, point)), width));
  return result;
}

export function bevelEdges(source: MeshData, edgeIds: number[], width: number, segments = 1): MeshOperationResult {
  const mesh = cloneMesh(source);
  const topology = deriveTopology(mesh);
  const selectedEdges = edgeIds.map((id) => topology.edgeById.get(id)).filter((edge): edge is DerivedEdge => Boolean(edge));
  if (!selectedEdges.length) throw new Error("Select one or more edges before beveling.");
  if (selectedEdges.some((edge) => edge.faceIds.length !== 2)) throw new Error("Bevel currently requires manifold interior edges with exactly two adjacent faces.");
  const positions = vertexMap(mesh);
  const minimumLength = Math.min(...selectedEdges.map((edge) => length3(sub3(positions.get(edge.a)!.position, positions.get(edge.b)!.position))));
  const safeWidth = clamp(Math.abs(width), 0.001, minimumLength * 0.45);
  segments = Math.max(1, Math.round(segments));
  const selectedKeys = new Set(selectedEdges.map((edge) => edge.key));
  const cornerId = new Map<string, number>();
  const affectedOriginalVertices = new Set<number>();

  for (const face of mesh.faces) {
    const replacement: number[] = [];
    const replacementUvs: Vec2[] = [];
    for (let index = 0; index < face.vertices.length; index += 1) {
      const originalId = face.vertices[index];
      const point = incidentFaceCornerPoint(mesh, face, index, selectedKeys, safeWidth, positions);
      if (point) {
        const id = newVertex(mesh, point);
        cornerId.set(`${face.id}:${originalId}`, id);
        affectedOriginalVertices.add(originalId);
        replacement.push(id);
      } else {
        replacement.push(originalId);
      }
      replacementUvs.push(face.uvs[index] ?? [0, 0]);
    }
    face.vertices = replacement;
    face.uvs = replacementUvs;
  }

  const createdFaces: number[] = [];
  for (const edge of selectedEdges) {
    const [faceA, faceB] = edge.faceIds;
    const a1 = cornerId.get(`${faceA}:${edge.a}`)!;
    const b1 = cornerId.get(`${faceA}:${edge.b}`)!;
    const a2 = cornerId.get(`${faceB}:${edge.a}`)!;
    const b2 = cornerId.get(`${faceB}:${edge.b}`)!;
    let previousA = a1;
    let previousB = b1;
    for (let segment = 1; segment <= segments; segment += 1) {
      const t = segment / segments;
      const nextA = segment === segments ? a2 : newVertex(mesh, lerp3(positionsFor(mesh, a1), positionsFor(mesh, a2), t));
      const nextB = segment === segments ? b2 : newVertex(mesh, lerp3(positionsFor(mesh, b1), positionsFor(mesh, b2), t));
      const bevelFace = newFace(mesh, [previousA, previousB, nextB, nextA], mesh.faces.find((face) => face.id === faceA)?.materialId ?? DEFAULT_MATERIAL);
      mesh.faces.push(bevelFace);
      createdFaces.push(bevelFace.id);
      previousA = nextA;
      previousB = nextB;
    }
  }

  for (const originalVertexId of affectedOriginalVertices) {
    const capIds = [...cornerId.entries()]
      .filter(([key]) => key.endsWith(`:${originalVertexId}`))
      .map(([, id]) => id);
    const stillUsed = mesh.faces.some((face) => face.vertices.includes(originalVertexId));
    if (stillUsed) capIds.push(originalVertexId);
    const unique = [...new Set(capIds)];
    if (unique.length >= 3) {
      const center = positionsFor(mesh, originalVertexId);
      const normal = normalize3(average3(unique.filter((id) => id !== originalVertexId).map((id) => sub3(positionsFor(mesh, id), center))));
      const axis = Math.abs(normal[1]) < 0.9 ? normalize3(cross3(normal, [0, 1, 0])) : normalize3(cross3(normal, [1, 0, 0]));
      const tangent = normalize3(cross3(normal, axis));
      unique.sort((left, right) => {
        const l = sub3(positionsFor(mesh, left), center);
        const r = sub3(positionsFor(mesh, right), center);
        return Math.atan2(dot3(l, tangent), dot3(l, axis)) - Math.atan2(dot3(r, tangent), dot3(r, axis));
      });
      const cap = newFace(mesh, unique, DEFAULT_MATERIAL);
      mesh.faces.push(cap);
      createdFaces.push(cap.id);
    }
  }

  return {
    mesh: refreshEdgeIds(mesh),
    selectedIds: createdFaces,
    lineage: Object.fromEntries(edgeIds.map((id) => [`edge:${id}`, []])),
    warnings: safeWidth !== Math.abs(width) ? [`Bevel width was clamped to ${safeWidth.toFixed(3)} cm to prevent overlap.`] : [],
  };
}

function positionsFor(mesh: MeshData, id: number): Vec3 {
  const value = mesh.vertices.find((vertex) => vertex.id === id);
  if (!value) throw new Error(`Missing vertex ${id}.`);
  return value.position;
}

interface RingFace {
  faceId: number;
  startA: number;
  startB: number;
  oppositeA: number;
  oppositeB: number;
}

function collectEdgeRing(mesh: MeshData, startEdgeId: number): { faces: RingFace[]; edgeOrientations: Map<string, [number, number]>; warning?: string } {
  const topology = deriveTopology(mesh);
  const start = topology.edgeById.get(startEdgeId);
  if (!start) return { faces: [], edgeOrientations: new Map(), warning: "The selected edge no longer exists." };
  const edgeOrientations = new Map<string, [number, number]>([[start.key, [start.a, start.b]]]);
  const faces: RingFace[] = [];
  const visitedFaces = new Set<number>();
  const queue: Array<{ faceId: number; edgeA: number; edgeB: number }> = start.faceIds.map((faceId) => ({ faceId, edgeA: start.a, edgeB: start.b }));
  while (queue.length) {
    const item = queue.shift()!;
    if (visitedFaces.has(item.faceId)) continue;
    const face = mesh.faces.find((candidate) => candidate.id === item.faceId);
    if (!face || face.vertices.length !== 4) continue;
    let index = face.vertices.findIndex((id, i) => id === item.edgeA && face.vertices[(i + 1) % 4] === item.edgeB);
    if (index < 0) {
      index = face.vertices.findIndex((id, i) => id === item.edgeB && face.vertices[(i + 1) % 4] === item.edgeA);
      if (index < 0) continue;
      [item.edgeA, item.edgeB] = [item.edgeB, item.edgeA];
    }
    const oppositeA = face.vertices[(index + 3) % 4];
    const oppositeB = face.vertices[(index + 2) % 4];
    visitedFaces.add(face.id);
    faces.push({ faceId: face.id, startA: item.edgeA, startB: item.edgeB, oppositeA, oppositeB });
    const oppositeKey = edgeKey(oppositeA, oppositeB);
    edgeOrientations.set(oppositeKey, [oppositeA, oppositeB]);
    const opposite = topology.edges.find((edge) => edge.key === oppositeKey);
    if (!opposite) continue;
    for (const neighborFaceId of opposite.faceIds) {
      if (!visitedFaces.has(neighborFaceId)) queue.push({ faceId: neighborFaceId, edgeA: oppositeB, edgeB: oppositeA });
    }
  }
  return {
    faces,
    edgeOrientations,
    warning: faces.length < start.faceIds.length ? "The edge ring stopped at a triangle, n-gon, pole, or boundary." : undefined,
  };
}

export function insertEdgeLoop(source: MeshData, edgeId: number, position = 0.5, count = 1): MeshOperationResult {
  const mesh = cloneMesh(source);
  const ring = collectEdgeRing(mesh, edgeId);
  if (!ring.faces.length) throw new Error(ring.warning ?? "Insert Edge Loop needs a quad edge ring.");
  position = clamp(position, 0.02, 0.98);
  count = Math.max(1, Math.round(count));
  const positions = vertexMap(mesh);
  const pointsByEdge = new Map<string, number[]>();
  for (const [key, orientation] of ring.edgeOrientations) {
    const [a, b] = orientation;
    const ids: number[] = [];
    for (let index = 0; index < count; index += 1) {
      const spread = count === 1 ? position : (index + 1) / (count + 1);
      ids.push(newVertex(mesh, lerp3(positions.get(a)!.position, positions.get(b)!.position, spread)));
    }
    pointsByEdge.set(key, ids);
  }
  const createdFaceIds: number[] = [];
  const affected = new Set(ring.faces.map((item) => item.faceId));
  const nextFaces: Face[] = mesh.faces.filter((face) => !affected.has(face.id));
  for (const item of ring.faces) {
    const original = mesh.faces.find((face) => face.id === item.faceId)!;
    const startPoints = pointsByEdge.get(edgeKey(item.startA, item.startB))!;
    const startOrientation = ring.edgeOrientations.get(edgeKey(item.startA, item.startB))!;
    const starts = startOrientation[0] === item.startA ? startPoints : [...startPoints].reverse();
    const oppositePoints = pointsByEdge.get(edgeKey(item.oppositeA, item.oppositeB))!;
    const oppositeOrientation = ring.edgeOrientations.get(edgeKey(item.oppositeA, item.oppositeB))!;
    const opposites = oppositeOrientation[0] === item.oppositeA ? oppositePoints : [...oppositePoints].reverse();
    const strips: number[][] = [];
    strips.push([item.startA, starts[0], opposites[0], item.oppositeA]);
    for (let index = 1; index < count; index += 1) {
      strips.push([starts[index - 1], starts[index], opposites[index], opposites[index - 1]]);
    }
    strips.push([starts[count - 1], item.startB, item.oppositeB, opposites[count - 1]]);
    strips.forEach((vertices, index) => {
      const face = index === 0
        ? { ...original, vertices, uvs: vertices.map(() => [0, 0] as Vec2) }
        : newFace(mesh, vertices, original.materialId);
      nextFaces.push(face);
      createdFaceIds.push(face.id);
    });
  }
  mesh.faces = nextFaces;
  const refreshed = refreshEdgeIds(mesh);
  const newEdgeIds = [...pointsByEdge.values()].flatMap((ids) => ids).flatMap((vertexId) => {
    const topology = deriveTopology(refreshed);
    return topology.vertexEdges.get(vertexId) ?? [];
  });
  return {
    mesh: refreshed,
    selectedIds: [...new Set(newEdgeIds)],
    lineage: { [`edge:${edgeId}`]: [...new Set(newEdgeIds)] },
    warnings: ring.warning ? [ring.warning] : [],
  };
}

export function transformVertices(source: MeshData, vertexIds: number[], matrix: number[]): MeshData {
  const mesh = cloneMesh(source);
  const selected = new Set(vertexIds);
  for (const vertex of mesh.vertices) {
    if (!selected.has(vertex.id)) continue;
    const [x, y, z] = vertex.position;
    const w = matrix[3] * x + matrix[7] * y + matrix[11] * z + matrix[15];
    vertex.position = [
      (matrix[0] * x + matrix[4] * y + matrix[8] * z + matrix[12]) / (w || 1),
      (matrix[1] * x + matrix[5] * y + matrix[9] * z + matrix[13]) / (w || 1),
      (matrix[2] * x + matrix[6] * y + matrix[10] * z + matrix[14]) / (w || 1),
    ];
  }
  return mesh;
}

export function componentVertexIds(mesh: MeshData, mode: "vertex" | "edge" | "face", componentIds: number[]): number[] {
  if (mode === "vertex") return [...new Set(componentIds)];
  if (mode === "face") {
    const selected = new Set(componentIds);
    return [...new Set(mesh.faces.filter((face) => selected.has(face.id)).flatMap((face) => face.vertices))];
  }
  const topology = deriveTopology(mesh);
  return [...new Set(componentIds.flatMap((id) => {
    const edge = topology.edgeById.get(id);
    return edge ? [edge.a, edge.b] : [];
  }))];
}

export function selectEdgeLoop(mesh: MeshData, startEdgeId: number): number[] {
  const topology = deriveTopology(mesh);
  const start = topology.edgeById.get(startEdgeId);
  if (!start) return [];
  const positions = vertexMap(mesh);
  const selected = new Set<number>([start.id]);
  const walk = (from: number, at: number, currentEdgeId: number): void => {
    let previous = from;
    let vertex = at;
    let edgeId = currentEdgeId;
    for (let step = 0; step < topology.edges.length; step += 1) {
      const incident = (topology.vertexEdges.get(vertex) ?? []).filter((id) => id !== edgeId && !selected.has(id));
      if (!incident.length) break;
      const incoming = normalize3(sub3(positions.get(vertex)!.position, positions.get(previous)!.position));
      const candidates = incident.map((id) => {
        const edge = topology.edgeById.get(id)!;
        const other = edge.a === vertex ? edge.b : edge.a;
        const direction = normalize3(sub3(positions.get(other)!.position, positions.get(vertex)!.position));
        return { id, other, score: dot3(incoming, direction) };
      }).sort((a, b) => b.score - a.score);
      if (!candidates.length || candidates[0].score < 0.15) break;
      const next = candidates[0];
      selected.add(next.id);
      previous = vertex;
      vertex = next.other;
      edgeId = next.id;
      if (vertex === from) break;
    }
  };
  walk(start.a, start.b, start.id);
  walk(start.b, start.a, start.id);
  return [...selected];
}

export function selectEdgePath(mesh: MeshData, startEdgeId: number, endEdgeId: number): number[] {
  const loop = selectEdgeLoop(mesh, startEdgeId);
  if (!loop.includes(endEdgeId)) return [];
  const topology = deriveTopology(mesh);
  const adjacency = new Map<number, number[]>();
  for (const left of loop) {
    const edgeA = topology.edgeById.get(left)!;
    for (const right of loop) {
      if (left === right) continue;
      const edgeB = topology.edgeById.get(right)!;
      if ([edgeA.a, edgeA.b].some((id) => id === edgeB.a || id === edgeB.b)) {
        const list = adjacency.get(left) ?? [];
        list.push(right);
        adjacency.set(left, list);
      }
    }
  }
  const queue: number[][] = [[startEdgeId]];
  const visited = new Set<number>();
  while (queue.length) {
    const path = queue.shift()!;
    const last = path[path.length - 1];
    if (last === endEdgeId) return path;
    if (visited.has(last)) continue;
    visited.add(last);
    for (const next of adjacency.get(last) ?? []) queue.push([...path, next]);
  }
  return [];
}

export function selectEdgeRing(mesh: MeshData, startEdgeId: number): number[] {
  const ring = collectEdgeRing(mesh, startEdgeId);
  return [...ring.edgeOrientations.keys()].map((key) => mesh.edgeIds[key]).filter((id): id is number => id !== undefined);
}

export function selectFaceLoop(mesh: MeshData, startFaceId: number, directionFaceId: number): number[] {
  const topology = deriveTopology(mesh);
  const start = mesh.faces.find((face) => face.id === startFaceId);
  const direction = mesh.faces.find((face) => face.id === directionFaceId);
  if (!start || !direction || start.vertices.length !== 4 || direction.vertices.length !== 4) return [];
  const shared = topology.edges.find((edge) => edge.faceIds.includes(startFaceId) && edge.faceIds.includes(directionFaceId));
  if (!shared) return [];
  const selected = new Set<number>([startFaceId, directionFaceId]);
  const walk = (faceId: number, enteringEdgeId: number): void => {
    let currentFaceId = faceId;
    let currentEdgeId = enteringEdgeId;
    for (let step = 0; step < mesh.faces.length; step += 1) {
      const face = mesh.faces.find((candidate) => candidate.id === currentFaceId);
      const halfEdges = topology.faceHalfEdges.get(currentFaceId);
      if (!face || face.vertices.length !== 4 || !halfEdges) break;
      const edgeIndex = halfEdges.findIndex((halfEdge) => halfEdge.edgeId === currentEdgeId);
      if (edgeIndex < 0) break;
      const opposite = halfEdges[(edgeIndex + 2) % 4];
      const edge = topology.edgeById.get(opposite.edgeId);
      const nextFaceId = edge?.faceIds.find((id) => id !== currentFaceId);
      if (!nextFaceId || selected.has(nextFaceId)) break;
      const nextFace = mesh.faces.find((candidate) => candidate.id === nextFaceId);
      if (!nextFace || nextFace.vertices.length !== 4) break;
      selected.add(nextFaceId);
      currentFaceId = nextFaceId;
      currentEdgeId = opposite.edgeId;
    }
  };
  walk(directionFaceId, shared.id);
  walk(startFaceId, shared.id);
  return [...selected];
}

function dominantAxis(normal: Vec3): { index: number; sign: number } {
  const values = normal.map(Math.abs);
  const index = values.indexOf(Math.max(...values));
  return { index, sign: normal[index] >= 0 ? 1 : -1 };
}

export function projectBoxUV(source: MeshData, scale = 1, rotation = 0, offset: Vec2 = [0, 0]): MeshData {
  const mesh = cloneMesh(source);
  const bounds = meshBounds(mesh);
  const positions = vertexMap(mesh);
  for (const face of mesh.faces) {
    const normal = getFaceNormal(mesh, face);
    const { index, sign } = dominantAxis(normal);
    const chart = index * 2 + (sign < 0 ? 1 : 0);
    const col = chart % 3;
    const row = Math.floor(chart / 3);
    face.uvs = face.vertices.map((id) => {
      const p = positions.get(id)!.position;
      const axes = index === 0 ? [2, 1] : index === 1 ? [0, 2] : [0, 1];
      const local: Vec2 = [
        (p[axes[0]] - bounds.min[axes[0]]) / (bounds.size[axes[0]] || 1) - 0.5,
        (p[axes[1]] - bounds.min[axes[1]]) / (bounds.size[axes[1]] || 1) - 0.5,
      ];
      const transformed = rotate2([local[0] * scale, local[1] * scale], rotation);
      return [(transformed[0] + 0.5 + col) / 3 + offset[0], (transformed[1] + 0.5 + row) / 2 + offset[1]];
    });
  }
  return mesh;
}

export function projectPlanarUV(
  source: MeshData,
  axis: "x" | "y" | "z" = "y",
  scale = 1,
  rotation = 0,
  offset: Vec2 = [0, 0],
): MeshData {
  const mesh = cloneMesh(source);
  const bounds = meshBounds(mesh);
  const positions = vertexMap(mesh);
  const axes = axis === "x" ? [2, 1] : axis === "y" ? [0, 2] : [0, 1];
  for (const face of mesh.faces) {
    face.uvs = face.vertices.map((id) => {
      const p = positions.get(id)!.position;
      const local: Vec2 = [
        (p[axes[0]] - bounds.min[axes[0]]) / (bounds.size[axes[0]] || 1) - 0.5,
        (p[axes[1]] - bounds.min[axes[1]]) / (bounds.size[axes[1]] || 1) - 0.5,
      ];
      const transformed = rotate2([local[0] * scale, local[1] * scale], rotation);
      return [transformed[0] + 0.5 + offset[0], transformed[1] + 0.5 + offset[1]];
    });
  }
  return mesh;
}

export function triangulateFace(face: Face): Array<[number, number, number]> {
  const result: Array<[number, number, number]> = [];
  for (let index = 1; index < face.vertices.length - 1; index += 1) result.push([0, index, index + 1]);
  return result;
}

export function uvDiagnostics(mesh: MeshData): { outOfRange: number; degenerate: number; triangleCount: number } {
  let outOfRange = 0;
  let degenerate = 0;
  let triangleCount = 0;
  for (const face of mesh.faces) {
    for (const uv of face.uvs) if (uv[0] < 0 || uv[0] > 1 || uv[1] < 0 || uv[1] > 1) outOfRange += 1;
    for (const [a, b, c] of triangulateFace(face)) {
      triangleCount += 1;
      const ab: Vec2 = [face.uvs[b][0] - face.uvs[a][0], face.uvs[b][1] - face.uvs[a][1]];
      const ac: Vec2 = [face.uvs[c][0] - face.uvs[a][0], face.uvs[c][1] - face.uvs[a][1]];
      if (Math.abs(ab[0] * ac[1] - ab[1] * ac[0]) < 1e-8) degenerate += 1;
    }
  }
  return { outOfRange, degenerate, triangleCount };
}

